import React from 'react';
import { RichTextElement } from 'coop-adventure-engine';
import { renderRichText } from '../../utils/richTextRenderer';

export interface InlineOptionProps {
  element: RichTextElement;
  onOptionClick: (optionId: string) => void;
  disabled?: boolean;
  className?: string;
  customColor?: string; // Optional custom button color from parent
}

const InlineOption: React.FC<InlineOptionProps> = ({
  element,
  onOptionClick,
  disabled = false,
  className = '',
  customColor
}) => {
  // Ensure this is an inline-option element
  if ((element as any).type !== 'inline-option') {
    return null;
  }

  const { content } = element;

  // Get the optionId from the element - this should be set during parsing
  const optionId = (element as any).optionId;

  if (!optionId) {
    console.warn('InlineOption: Missing optionId property');
    return null;
  }

  const handleClick = () => {
    if (!disabled) {
      onOptionClick(optionId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  // Render the content - it should be an array of RichTextElement
  const renderContent = () => {
    if (!content || !Array.isArray(content)) {
      return '';
    }

    // Create a proper RichTextDescription structure
    const richTextDescription = { elements: content };
    
    // Render the rich text content
    return renderRichText(richTextDescription);
  };

  // Generate custom color styles if customColor is provided
  const getCustomColorStyles = () => {
    if (!customColor) return {};
    
    // Convert hex color to rgba with different opacities for gradient effect
    const getColorWithAlpha = (color: string, alpha: string) => {
      if (color.startsWith('#')) {
        return `${color}${alpha}`;
      }
      // For rgb/hsl, we'll use the color as-is with opacity in background
      return color;
    };
    
    return {
      background: `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`,
      backdropFilter: 'blur(12px) saturate(150%)',
      WebkitBackdropFilter: 'blur(12px) saturate(150%)',
      border: `1px solid ${getColorWithAlpha(customColor, '40')}`,
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
      WebkitFontSmoothing: 'antialiased' as const,
      MozOsxFontSmoothing: 'grayscale' as const,
    };
  };

  const customColorStyles = getCustomColorStyles();

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`inline-option ${customColor ? 'custom-color' : ''} ${className}`}
      style={customColorStyles}
      aria-label={`Option: ${optionId}`}
      role="button"
      data-testid="inline-option"
      data-option-id={optionId}
    >
      <span 
        className="inline-option-content"
        dangerouslySetInnerHTML={{ __html: renderContent() }}
      />
    </button>
  );
};

export default InlineOption;
